/**
 * Per-character width data for Zebra built-in Font 0 (CG Triumvirate Bold Condensed).
 *
 * Each value is the Horizontal Escape number from Zebra's official documentation.
 * Actual character width = (horizontalEscape / FONT_0_DIVISOR) * fontWidth.
 *
 * Source: https://support.zebra.com/cpws/docs/fonts/zero_font_calc.htm
 */

const FONT_0_DIVISOR = 8782;

/** Horizontal Escape values indexed by ASCII code (32–127). */
const FONT_0_ESCAPES: Record<number, number> = {
  32: 2602, // space
  33: 2602, // !
  34: 4228, // "
  35: 4228, // #
  36: 4228, // $
  37: 7969, // %
  38: 5367, // &
  39: 2602, // '
  40: 2602, // (
  41: 2602, // )
  42: 4228, // *
  43: 7969, // +
  44: 2602, // ,
  45: 2602, // -
  46: 2602, // .
  47: 2602, // /
  48: 4228, // 0
  49: 4228, // 1
  50: 4228, // 2
  51: 4228, // 3
  52: 4228, // 4
  53: 4228, // 5
  54: 4228, // 6
  55: 4228, // 7
  56: 4228, // 8
  57: 4228, // 9
  58: 2602, // :
  59: 2602, // ;
  60: 8782, // <
  61: 7969, // =
  62: 8782, // >
  63: 3903, // ?
  64: 7969, // @
  65: 4879, // A
  66: 4879, // B
  67: 4716, // C
  68: 5204, // D
  69: 4391, // E
  70: 4391, // F
  71: 5204, // G
  72: 5367, // H
  73: 2439, // I
  74: 3903, // J
  75: 4879, // K
  76: 4228, // L
  77: 6668, // M
  78: 5367, // N
  79: 5042, // O
  80: 4879, // P
  81: 5042, // Q
  82: 5204, // R
  83: 4716, // S
  84: 4391, // T
  85: 5367, // U
  86: 4716, // V
  87: 7156, // W
  88: 4879, // X
  89: 4879, // Y
  90: 4391, // Z
  91: 2602, // [
  92: 2602, // backslash
  93: 2602, // ]
  94: 4391, // ^
  95: 4391, // _
  96: 2602, // `
  97: 4066, // a
  98: 4391, // b
  99: 3903, // c
  100: 4391, // d
  101: 4228, // e
  102: 2439, // f
  103: 4391, // g
  104: 4391, // h
  105: 2277, // i
  106: 2277, // j
  107: 3903, // k
  108: 2277, // l
  109: 6668, // m
  110: 4391, // n
  111: 4228, // o
  112: 4391, // p
  113: 4391, // q
  114: 2927, // r
  115: 3740, // s
  116: 2439, // t
  117: 4391, // u
  118: 3903, // v
  119: 5855, // w
  120: 3903, // x
  121: 3903, // y
  122: 3415, // z
  123: 4391, // {
  124: 4391, // |
  125: 4391, // }
  126: 8782, // ~
  127: 8782, // DEL
};

/** Average escape for unknown characters within Font 0. */
const FONT_0_FALLBACK_ESCAPE = 4228; // same as digits/common chars

const FIXED_WIDTH_FAMILIES = new Set(["A", "B", "C", "D", "E", "F", "G", "H"]);

function isFont0(family: string): boolean {
  return family.trim() === "0";
}

function isFixedWidth(family: string): boolean {
  return FIXED_WIDTH_FAMILIES.has(family.trim().toUpperCase());
}

/**
 * Returns whether per-character width metrics are available for a font family.
 */
export function hasPerCharMetrics(fontFamily: string): boolean {
  return isFont0(fontFamily) || isFixedWidth(fontFamily);
}

/**
 * Returns the width of a character as a fraction of the specified font width (0.0–1.0).
 * Returns `undefined` for font families without per-character data.
 */
export function charWidthFraction(
  char: string,
  fontFamily: string,
): number | undefined {
  if (isFont0(fontFamily)) {
    const code = char.charCodeAt(0);
    const escapeWidth = FONT_0_ESCAPES[code] ?? FONT_0_FALLBACK_ESCAPE;
    return escapeWidth / FONT_0_DIVISOR;
  }

  if (isFixedWidth(fontFamily)) {
    return 1.0;
  }

  return undefined;
}

/**
 * Creates a character measurement function that returns width in mm.
 * Only Font 0 (per-char table) and fixed-width fonts (A–H) are supported.
 */
export function createCharMeasure(
  fontFamily: string,
  fontWidthMm: number,
): (char: string) => number {
  if (isFont0(fontFamily)) {
    return (char: string) => {
      const code = char.charCodeAt(0);
      const escapeWidth = FONT_0_ESCAPES[code] ?? FONT_0_FALLBACK_ESCAPE;
      return (escapeWidth / FONT_0_DIVISOR) * fontWidthMm;
    };
  }

  if (isFixedWidth(fontFamily)) {
    return () => fontWidthMm;
  }

  throw new Error(
    `Font family "${fontFamily}" has no per-character width metrics. Only Font 0 and fixed-width fonts (A–H) are supported.`,
  );
}

/**
 * Measures the total rendered width of a string in mm.
 *
 * In hot paths that measure many strings with the same font settings, pass a
 * pre-created `measure` function from `createCharMeasure` to avoid allocating
 * a new closure on every call.
 */
export function measureTextWidthMm(
  text: string,
  fontFamily: string,
  fontWidthMm: number,
  measure: (char: string) => number = createCharMeasure(
    fontFamily,
    fontWidthMm,
  ),
): number {
  let width = 0;
  for (let i = 0; i < text.length; i++) {
    width += measure(text.charAt(i));
  }
  return width;
}
